/**
 * PATH: packages/ui/src/kanban/utils/localDate.ts
 * FILE_ID: KANBANUI-LOCALDATE-001
 * PURPOSE: THE canonical local-calendar date helpers for every Kanban time
 *          view (Gantt timeline, Gantt buckets, Calendar, Cash-flow, Inbox
 *          rail, due-date filters, reminders).
 * WHY: UAT P0 "time conversion broken" (docs/uat/KANBAN_UAT_SCOPE.md §2).
 *      Three views independently mixed `Date.toISOString().slice(0,10)`
 *      (UTC calendar) with local-midnight Dates. In Sydney (UTC+10) the
 *      "today" marker, Schedule-onto-today and inbox overdue grouping all
 *      pointed at YESTERDAY between local midnight and 10 AM; in the
 *      Americas (UTC−) Gantt bars drew one column LEFT of their headers.
 *      Two correct private fixes already existed (BoardGanttBucketView.
 *      localIsoDate, BoardCashFlowView.helpers.toLocalISODate) — this file
 *      promotes that logic to ONE shared module so no view re-invents it.
 * FLOW:
 *   ┌──────────────┐   ┌─────────────────────┐   ┌──────────────────┐
 *   │ Date / YMD   │ → │ local-calendar math │ → │ YMD / Date / int │
 *   └──────────────┘   └─────────────────────┘   └──────────────────┘
 * RULES OF THE ROAD:
 *   - Date objects in time views are anchored at LOCAL midnight.
 *   - Day arithmetic on YMD strings is done in UTC internally (no DST).
 *   - Day arithmetic on Dates uses setDate() (calendar-safe across DST).
 *   - Day DIFFERENCES between local midnights use Math.round, never
 *     Math.floor — a DST fold makes "midnight to midnight" 23h or 25h.
 * RELATED:
 *   - components/BoardGanttTimelineView.tsx (pixel math consumer)
 *   - components/BoardGanttBucketView.tsx (re-exports localIsoDate)
 *   - components/BoardCashFlowView.helpers.ts
 *   - utils/reminderBuckets.ts, components/gantt-timeline-schedule.ts
 */

export const DAY_MS = 24 * 3600 * 1000

/** Format a Date as YYYY-MM-DD on the OPERATOR's local calendar.
 *  Never use `toISOString().slice(0,10)` for this — that's the UTC
 *  calendar and shifts the day for any non-UTC operator. */
export function localYmd(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/** Today's YYYY-MM-DD on the operator's local calendar. */
export function todayLocalYmd(): string {
  return localYmd(new Date())
}

/** Parse a YYYY-MM-DD (datetime strings tolerated — date part wins)
 *  to a Date at LOCAL midnight. The inverse of `localYmd`. */
export function parseYmdLocal(ymd: string): Date {
  const [y, m, d] = ymd.slice(0, 10).split('-').map(Number)
  return new Date(y, (m || 1) - 1, d || 1)
}

/** Add whole days to a YYYY-MM-DD string, returning YYYY-MM-DD.
 *  Internally UTC so the arithmetic is DST-free; pure string-in →
 *  string-out day math is timezone-independent. */
export function addDaysYmd(ymd: string, days: number): string {
  const d = new Date(`${ymd.slice(0, 10)}T00:00:00Z`)
  d.setUTCDate(d.getUTCDate() + days)
  return d.toISOString().slice(0, 10)
}

/** Add whole CALENDAR days to a Date, preserving the local wall-clock
 *  time (setDate handles DST — no 23h/25h drift like `+ n*DAY_MS`). */
export function addDaysLocal(d: Date, days: number): Date {
  const out = new Date(d.getTime())
  out.setDate(out.getDate() + days)
  return out
}

/** Monday 00:00 local of the calendar week that contains `d`.
 *  ISO-style week (Mon–Sun). Sunday belongs to the week that started
 *  the previous Monday — never the following Monday. */
export function startOfWeekMonday(d: Date): Date {
  const local = new Date(d.getFullYear(), d.getMonth(), d.getDate())
  const dow = local.getDay() // 0=Sun … 6=Sat
  const delta = dow === 0 ? -6 : 1 - dow
  return addDaysLocal(local, delta)
}

/** Whole-day difference `a − b` between two local-midnight Dates.
 *  Math.round (NOT floor) so a DST fold (23h/25h day) still counts
 *  as exactly one day. */
export function diffLocalDays(a: Date, b: Date): number {
  return Math.round((a.getTime() - b.getTime()) / DAY_MS)
}

/** Whole-day difference `a − b` between two YYYY-MM-DD strings.
 *  Exact (UTC-internal, no DST). */
export function diffCalendarDays(aYmd: string, bYmd: string): number {
  const a = new Date(`${aYmd.slice(0, 10)}T00:00:00Z`).getTime()
  const b = new Date(`${bYmd.slice(0, 10)}T00:00:00Z`).getTime()
  return Math.round((a - b) / DAY_MS)
}

// ── Display formatters (dd/mm/yyyy) ──────────────────────────────────────
// Operator ask 2026-06-29: "cards, activity, etc dates should be dd/mm/yyyy".
// One canonical day-first formatter so every Kanban surface reads the same
// way and a bare date can't be misread (e.g. is "06-10" June-10 or Oct-6?).
// Date-only YMD strings are read as a plain calendar date (no timezone
// shift); full datetimes are localised to the operator's clock.

/** `dd/mm/yyyy` on the operator's local calendar. '' for empty input;
 *  echoes the raw value back on an unparseable string (never throws). */
export function formatDmy(iso: string | null | undefined): string {
  if (!iso) return ''
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(iso)
  const d = dateOnly ? parseYmdLocal(iso) : new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  const day = String(d.getDate()).padStart(2, '0')
  const m = String(d.getMonth() + 1).padStart(2, '0')
  return `${day}/${m}/${d.getFullYear()}`
}

/** `dd/mm/yyyy, HH:mm` (24-hour) on the operator's local calendar. */
export function formatDmyTime(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  const hh = String(d.getHours()).padStart(2, '0')
  const mm = String(d.getMinutes()).padStart(2, '0')
  return `${formatDmy(iso)}, ${hh}:${mm}`
}

/** Compact `dd/mm` (day-first, no year) for dense card-face pills. */
export function formatDmShort(iso: string | null | undefined): string {
  if (!iso) return ''
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(iso)
  const d = dateOnly ? parseYmdLocal(iso) : new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const day = String(d.getDate()).padStart(2, '0')
  const m = String(d.getMonth() + 1).padStart(2, '0')
  return `${day}/${m}`
}

// Deterministic short month names (no Intl) so the card-face date reads the
// same for every operator and tests don't hinge on the JS engine's locale data.
const MONTHS_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
] as const

/** Spelled-out `d Mon yy` (e.g. `22 Jul 26`) for the card-face date badge.
 *  Operator 2026-07-01: "dates should be more obvious … like spell out
 *  22 Jul 26" — a bare `dd/mm` reads as an anonymous number on a dense tile.
 *  Day-first + named month kills the "is 06-07 June-7 or Jul-6?" ambiguity;
 *  the 2-digit year keeps it compact while still anchoring far-out dates.
 *  '' for empty/unparseable input (never throws). */
export function formatDmMonthShort(iso: string | null | undefined): string {
  if (!iso) return ''
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(iso)
  const d = dateOnly ? parseYmdLocal(iso) : new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const yy = String(d.getFullYear()).slice(-2)
  return `${d.getDate()} ${MONTHS_SHORT[d.getMonth()]} ${yy}`
}
